import { useState, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  useItemAttachments,
  useEmailAttachments,
  useTripAttachments,
  useUploadAttachment,
  useDeleteAttachment,
  useAttachmentUrl,
  useRawEmail,
  type ItemType,
  type Attachment,
} from "@/hooks/useAttachments";
import {
  FileText,
  Image,
  Download,
  Trash2,
  Upload,
  Mail,
  Eye,
  Loader2,
  Paperclip,
  ExternalLink,
} from "lucide-react";
import { safeFormat } from "@/lib/formatDateTime";

interface AttachmentsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tripId: string;
  itemType: ItemType;
  itemIndex: number;
  itemTitle: string;
  sourceEmailId?: string;
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith("image/")) {
    return <Image className="h-5 w-5 text-blue-500" />;
  }
  if (contentType === "application/pdf") {
    return <FileText className="h-5 w-5 text-red-500" />;
  }
  return <FileText className="h-5 w-5 text-gray-500" />;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentRow({ attachment }: { attachment: Attachment }) {
  const { data: signedUrl, isLoading: urlLoading } = useAttachmentUrl(attachment.storage_path);
  const deleteAttachment = useDeleteAttachment();
  const [showPreview, setShowPreview] = useState(false);

  const handleView = () => {
    if (signedUrl) {
      if (attachment.content_type.startsWith("image/")) {
        setShowPreview(true);
      } else {
        window.open(signedUrl, "_blank");
      }
    }
  };

  const handleDownload = () => {
    if (signedUrl) {
      const a = document.createElement("a");
      a.href = signedUrl;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this attachment?")) {
      deleteAttachment.mutate(attachment);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 py-3 px-3 hover:bg-muted/50 rounded-lg transition-colors">
        <div className="flex-shrink-0">
          {getFileIcon(attachment.content_type)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{attachment.filename}</p>
          <p className="text-xs text-muted-foreground">
            {formatFileSize(attachment.file_size)} · {" "}
            {attachment.source === "email" ? "From email" : "Uploaded"} · {" "}
            {safeFormat(attachment.created_at, "MMM d, yyyy 'at' h:mm a")}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleView}
            disabled={urlLoading}
            title="View"
          >
            {urlLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDownload}
            disabled={urlLoading}
            title="Download"
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDelete}
            disabled={deleteAttachment.isPending}
            title="Delete"
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Image Preview Dialog */}
      {showPreview && signedUrl && (
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{attachment.filename}</DialogTitle>
            </DialogHeader>
            <div className="flex justify-center">
              <img
                src={signedUrl}
                alt={attachment.filename}
                className="max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function SourceEmailSection({ sourceEmailId }: { sourceEmailId: string }) {
  const { data: email, isLoading } = useRawEmail(sourceEmailId);
  const { data: emailAttachments } = useEmailAttachments(sourceEmailId);
  const [showEmail, setShowEmail] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!email) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Mail className="h-4 w-4" />
        <span>Source Email</span>
      </div>

      <div className="bg-muted/50 rounded-lg p-4 space-y-3">
        <p className="font-medium">{email.subject || "(No subject)"}</p>
        <div className="text-sm text-muted-foreground space-y-1">
          <p>From: {email.from_address}</p>
          <p>Received: {safeFormat(email.received_at, "MMM d, yyyy 'at' h:mm a")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowEmail(true)}>
          <ExternalLink className="h-3 w-3 mr-2" />
          View Full Email
        </Button>
      </div>

      {/* Email attachments */}
      {emailAttachments && emailAttachments.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            Email Attachments ({emailAttachments.length})
          </p>
          <div className="space-y-1 bg-muted/30 rounded-lg p-2">
            {emailAttachments.map((att) => (
              <AttachmentRow key={att.id} attachment={att} />
            ))}
          </div>
        </div>
      )}

      {/* Full Email Dialog */}
      <Dialog open={showEmail} onOpenChange={setShowEmail}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{email.subject || "(No subject)"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground space-y-1">
              <p><strong>From:</strong> {email.from_address}</p>
              <p><strong>Received:</strong> {safeFormat(email.received_at, "PPpp")}</p>
            </div>
            <Separator />
            {email.body_html ? (
              <div
                className="prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: email.body_html }}
              />
            ) : email.body_text ? (
              <pre className="whitespace-pre-wrap text-sm font-mono bg-muted p-4 rounded-lg overflow-x-auto">
                {email.body_text}
              </pre>
            ) : (
              <p className="text-muted-foreground italic">No email content available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AttachmentsSheet({
  open,
  onOpenChange,
  tripId,
  itemType,
  itemIndex,
  itemTitle,
  sourceEmailId,
}: AttachmentsSheetProps) {
  // Get item-specific attachments (user uploads)
  const { data: itemAttachments, isLoading: itemLoading } = useItemAttachments(tripId, itemType, itemIndex);
  // Get all trip attachments (for showing email attachments not linked to specific items)
  const { data: tripAttachments, isLoading: tripLoading } = useTripAttachments(tripId);
  const uploadAttachment = useUploadAttachment();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter trip attachments to show unlinked ones from email
  const unlinkededEmailAttachments = tripAttachments?.filter(
    att => att.source === "email" && !att.item_type && !att.item_index
  ) || [];

  const isLoading = itemLoading || tripLoading;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    await uploadAttachment.mutateAsync({
      file,
      tripId,
      itemType,
      itemIndex,
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[500px] sm:max-w-[500px] flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Paperclip className="h-5 w-5" />
            Attachments
          </SheetTitle>
          <p className="text-sm text-muted-foreground">{itemTitle}</p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Item-specific Attachments */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">Uploaded Attachments</h3>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : itemAttachments && itemAttachments.length > 0 ? (
              <div className="space-y-1 bg-muted/30 rounded-lg p-2">
                {itemAttachments.map((att) => (
                  <AttachmentRow key={att.id} attachment={att} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground bg-muted/30 rounded-lg">
                <Paperclip className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No attachments uploaded</p>
              </div>
            )}

            {/* Upload Section */}
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx"
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadAttachment.isPending}
              >
                {uploadAttachment.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Attachment
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                PDF, images, or Word docs up to 10MB
              </p>
            </div>
          </div>

          {/* Unlinked Email Attachments */}
          {unlinkededEmailAttachments.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-medium">Trip Attachments (from emails)</h3>
                <p className="text-xs text-muted-foreground">
                  These attachments were imported from emails but not linked to specific items.
                </p>
                <div className="space-y-1 bg-muted/30 rounded-lg p-2">
                  {unlinkededEmailAttachments.map((att) => (
                    <AttachmentRow key={att.id} attachment={att} />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Source Email Section */}
          {sourceEmailId && (
            <>
              <Separator />
              <SourceEmailSection sourceEmailId={sourceEmailId} />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
